import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntil } from 'rxjs';
import { TuiEditorTool, TuiEditorToolType } from '@taiga-ui/editor';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { APP_CONFIG } from '@src/core';
import { AlertService } from '@src/core/services';
import {
  convertDateToTuiDayAndTuiTime,
  convertTuiDayAndTuiTimeToDate,
  getImageSrc,
  TuiDayTimeTransformer,
} from '@src/utils';
import { convertDateToApiFormat } from '@src/api';
import { ViewMode } from '@src/models';
import { TIME_ITEMS } from '@src/core/constants';

import { LoyaltyProgramCollection } from '../../types';
import { LoyaltyProgramCategory } from '../../../models';

import { FormDataType } from './types';
import { MAP_STATUS_API_TO_FORM, MAP_STATUS_FORM_TO_API } from './constants';
import { createForm, createSection } from './utils';

@Component({
  selector: 'lp-collection-edit',
  templateUrl: './lp-collection-edit.component.html',
  styleUrls: ['./lp-collection-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpCollectionEditComponent extends BaseResizableComponent implements OnChanges {
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  @Input() data?: LoyaltyProgramCollection | null;
  @Input() mode?: ViewMode;
  @Input() loading = true;
  @Input() unionId?: string;

  @Output() saveCollection = new EventEmitter<LoyaltyProgramCollection>();
  @Output() cancelEdit = new EventEmitter<void>();
  @Output() checkPush = new EventEmitter<void>();

  form = createForm();

  private bannerBackgroundPhotoUrl?: string;

  readonly maxDocFileSize = APP_CONFIG.fileSizeMax.image;

  readonly excludeEditorTools: TuiEditorToolType[] = [
    TuiEditorTool.Img,
    TuiEditorTool.Anchor,
    TuiEditorTool.Sub,
    TuiEditorTool.Sup,
    TuiEditorTool.Hilite,
    TuiEditorTool.Code,
    TuiEditorTool.Clear,
    TuiEditorTool.Quote,
  ];

  readonly timeItems = TIME_ITEMS;

  get sectionsControl() {
    return this.form.controls.sections;
  }

  get banner(): LoyaltyProgramCategory | undefined {
    return { id: 'preview', imageURL: this.bannerBackgroundPhotoUrl, name: this.form.value.bannerTitle ?? '' };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.loading = true;
      this.form.reset();

      if (this.data) {
        const formData = this.mapApiDataToFormData(this.data);
        formData.sections.forEach(() => this.addSection());
        this.form.patchValue(formData);
      } else {
        this.addSection();
      }

      this.loading = false;
    }

    this.cdr.markForCheck();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.form.controls.bannerBackground?.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(file => {
      this.bannerBackgroundPhotoUrl = file ? window.URL.createObjectURL(file) : undefined;
    });
  }

  addSection() {
    this.sectionsControl.push(createSection());
  }

  removeSection(index: number): void {
    this.sectionsControl.removeAt(index);
  }

  moveSection(currentIndex: number, index: number) {
    moveItemInArray(this.sectionsControl.controls, currentIndex, currentIndex + index);
  }

  onClickSaveButton() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      return;
    }

    const apiData = this.mapFormDataToApiData(this.form.getRawValue());

    this.saveCollection.emit(apiData);
  }

  private mapApiDataToFormData(collection: LoyaltyProgramCollection): FormDataType {
    const {
      id,
      status,
      title,
      validTill,
      sortOrder,

      pushTitle,
      pushText,
      pushSendOn,

      bannerBackgroundPhotoId,
      bannerTitle,
      sections,
    } = collection;

    const [validTillNormalize] = convertDateToTuiDayAndTuiTime(validTill);
    const [pushSendOnDay, pushSendOnTime] = convertDateToTuiDayAndTuiTime(pushSendOn);

    this.bannerBackgroundPhotoUrl = getImageSrc(bannerBackgroundPhotoId, undefined, undefined, this.unionId);

    return <FormDataType>{
      id,
      status: MAP_STATUS_API_TO_FORM[status],
      title,
      validTill: validTillNormalize,
      sortOrder,

      pushTitle,
      pushText,
      pushSendOnDay,
      pushSendOnTime,

      bannerTitle,
      bannerBackgroundPhotoId,

      sections,
    };
  }

  private mapFormDataToApiData(formData: FormDataType): LoyaltyProgramCollection {
    const {
      id,
      status,
      title,
      validTill,
      sortOrder,

      pushTitle,
      pushText,
      pushSendOnDay,
      pushSendOnTime,

      bannerBackground,
      bannerBackgroundPhotoId,
      bannerTitle,

      sections,
    } = formData;

    const validTillNormalize = TuiDayTimeTransformer.tuiDayToDate(validTill);

    const pushSendOnNormalize = convertTuiDayAndTuiTimeToDate(pushSendOnDay, pushSendOnTime ?? this.timeItems[0]);

    const sectionsNormalize = sections.filter(section => !!section.contentText || !!section.products.length);

    return <LoyaltyProgramCollection>{
      id,
      status: MAP_STATUS_FORM_TO_API[`${status}`],
      title,
      validTill: validTillNormalize ? formatDate(validTillNormalize, 'yyyy-MM-dd', 'ru') : undefined,
      sortOrder,

      pushTitle,
      pushText,
      pushSendOn: pushSendOnNormalize ? convertDateToApiFormat(pushSendOnNormalize, false) : undefined,

      bannerBackground,
      bannerBackgroundPhotoId,
      bannerTitle,

      sections: sectionsNormalize,
    };
  }
}
